/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Smart AI Writer & Document Editor View (Task 10.2.1 & 10.7.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { EditorToolbar } from '../components/editor/EditorToolbar';
import { TiptapEditor } from '../components/editor/TiptapEditor';
import { EditorStats } from '../components/editor/EditorStats';
import { useEditorStore } from '../stores/useEditorStore';
import { useI18n } from '../stores/useI18nStore';
import {
  Save,
  Download,
  FileText,
  RotateCcw,
  Sparkles,
  Check,
  Wand2,
} from 'lucide-react';

interface EditorViewProps {
  onOpenWizard?: () => void;
}

export const EditorView: React.FC<EditorViewProps> = ({ onOpenWizard }) => {
  const { title, setTitle, contentHtml, saveDraft, resetEditor } = useEditorStore();
  const { t } = useI18n();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [copiedNotification, setCopiedNotification] = useState(false);

  const handleExport = (format: 'markdown' | 'html' | 'text') => {
    let contentToExport = contentHtml;
    let mimeType = 'text/html';
    let extension = 'html';

    if (format === 'markdown' || format === 'text') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = contentHtml;
      contentToExport = tempDiv.textContent || tempDiv.innerText || '';
      mimeType = 'text/plain';
      extension = format === 'markdown' ? 'md' : 'txt';
    }

    const blob = new Blob([contentToExport], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.toLowerCase().replace(/\s+/g, '-') || 'document'}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportMenu(false);
  };

  const handleManualSave = () => {
    saveDraft();
    setCopiedNotification(true);
    setTimeout(() => setCopiedNotification(false), 2000);
  };

  const handleReset = () => {
    if (window.confirm(t('editor.resetConfirm'))) {
      resetEditor();
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* 1. Document Title & Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl border border-surface-border bg-surface shadow-sm">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('editor.placeholder')}
            className="w-full text-xl sm:text-2xl font-heading font-extrabold text-foreground bg-transparent border-none outline-none focus:ring-0 placeholder:text-foreground-muted/60"
          />
          <div className="flex items-center gap-2 mt-1 text-xs text-foreground-muted">
            <span className="flex items-center gap-1 font-medium text-primary">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{t('editor.active')}</span>
            </span>
            <span>•</span>
            <span>
              {t('editor.shortcutHint', {
                ctrlSlash: 'Ctrl + /',
                slash: '/',
              })}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {onOpenWizard && (
            <button
              type="button"
              onClick={onOpenWizard}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/30 bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-all shadow-sm active:scale-95"
              title={t('editor.openWizardTooltip')}
            >
              <Wand2 className="w-3.5 h-3.5" />
              <span>{t('editor.wizardBtn')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={handleReset}
            className="p-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
            title={t('editor.reset')}
            aria-label={t('editor.reset')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Export Menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-semibold hover:bg-muted transition-colors"
            >
              <Download className="w-4 h-4 text-foreground-muted" />
              <span>{t('editor.export')}</span>
            </button>

            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1.5 z-30 w-44 rounded-xl border border-surface-border bg-surface p-1 shadow-glass text-xs space-y-0.5 animate-scale-in">
                <button
                  type="button"
                  onClick={() => handleExport('markdown')}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-muted transition-colors"
                >
                  <FileText className="w-3.5 h-3.5 text-primary" />
                  <span>{t('editor.exportMarkdown')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('html')}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-muted transition-colors"
                >
                  <FileText className="w-3.5 h-3.5 text-accent" />
                  <span>{t('editor.exportHtml')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleExport('text')}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-muted transition-colors"
                >
                  <FileText className="w-3.5 h-3.5 text-foreground-muted" />
                  <span>{t('editor.exportText')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Save Draft CTA */}
          <button
            type="button"
            onClick={handleManualSave}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-bold hover:bg-primary-hover shadow-glow-primary/20 transition-all active:scale-95"
          >
            {copiedNotification ? (
              <>
                <Check className="w-4 h-4" />
                <span>{t('common.saved')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{t('editor.saveDraft')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 2. Top Formatting Toolbar */}
      <EditorToolbar editor={editor} />

      {/* 3. Main Tiptap ProseMirror Editor Viewport */}
      <TiptapEditor onEditorReady={setEditor} />

      {/* 4. Live Statistics & Status */}
      <EditorStats />
    </div>
  );
};
